import { isEmpty, sortBy } from 'lodash';

import {
  DeviceAssociatedPriceRule,
  DeviceAssociatedPriceRules,
  DeviceAssociatedPrices,
  IfpOfferDetail,
  IfpOfferDetails,
  PaymentOption,
  Section,
} from '../typings';

export const getLowestPriceOffer = (offers: PaymentOption[]) => {
  return offers?.slice().sort((a: PaymentOption, b: PaymentOption) => {
    if (a.effectivePrice && b.effectivePrice) {
      return a.effectivePrice - b.effectivePrice;
    }
    return a.basePrice - b.basePrice;
  })?.[0];
};

function getAssociatedPlanAndIfpIds(
  associatedPriceRule: DeviceAssociatedPriceRule,
) {
  return associatedPriceRule.sources.map((source) => source.sourceId);
}

function associatedPriceRuleMatcher(
  associatedPriceRule: DeviceAssociatedPriceRule,
  ifpId: string,
  planId: string,
) {
  const associatedPlanAndIfpIds =
    getAssociatedPlanAndIfpIds(associatedPriceRule);
  return (
    associatedPlanAndIfpIds.includes(ifpId) &&
    associatedPlanAndIfpIds.includes(planId)
  );
}

function constructIfpOptionWithGeneralPrice(
  associatedPrices: DeviceAssociatedPrices,
  ifpOffer: IfpOfferDetail,
) {
  const priceByIfpDuration = associatedPrices?.find((price) =>
    ifpOffer.id.includes(String(price.priceLength)),
  );

  if (!priceByIfpDuration) {
    return null;
  }

  return {
    id: ifpOffer.id,
    name: ifpOffer.name,
    description: ifpOffer.description,
    basePrice: priceByIfpDuration.basePrice,
    basePriceExcludingTax: priceByIfpDuration.basePriceExcludingTax,
    effectivePrice: priceByIfpDuration.effectivePrice,
    effectivePriceExcludingTax: priceByIfpDuration.effectivePriceExcludingTax,
    priceFrequencyPeriod: priceByIfpDuration.priceFrequencyPeriod,
    priceFrequencyValue: priceByIfpDuration.priceFrequencyValue,
    priceLength: priceByIfpDuration.priceLength,
    priceType: priceByIfpDuration.priceType,
    downPaymentRequired: priceByIfpDuration.downPaymentRequired,
    minimumDownPaymentAmount: priceByIfpDuration.minimumDownPaymentAmount,
    promotions: ifpOffer.promotions,
  };
}

function constructIfpOptionWithPriceRuleApplied(
  associatedPriceRules: DeviceAssociatedPriceRules,
  ifpOffer: IfpOfferDetail,
  planId: string,
) {
  const priceByPlanAndIfpCombination = associatedPriceRules?.find((rule) =>
    associatedPriceRuleMatcher(rule, ifpOffer.id, planId),
  );

  if (!priceByPlanAndIfpCombination) {
    return null;
  }

  return {
    id: ifpOffer.id,
    name: ifpOffer.name,
    description: ifpOffer.description,
    associatedPriceRuleDescription: priceByPlanAndIfpCombination.description,
    basePrice: priceByPlanAndIfpCombination.basePrice,
    basePriceExcludingTax: priceByPlanAndIfpCombination.basePriceExcludingTax,
    effectivePrice: priceByPlanAndIfpCombination.effectivePrice,
    effectivePriceExcludingTax:
      priceByPlanAndIfpCombination.effectivePriceExcludingTax,
    priceFrequencyPeriod: priceByPlanAndIfpCombination.priceFrequencyPeriod,
    priceFrequencyValue: priceByPlanAndIfpCombination.priceFrequencyValue,
    priceLength: priceByPlanAndIfpCombination.priceLength,
    priceType: priceByPlanAndIfpCombination.priceType,
    downPaymentRequired: priceByPlanAndIfpCombination.downPaymentRequired,
    minimumDownPaymentAmount:
      priceByPlanAndIfpCombination.minimumDownPaymentAmount,
    promotions: ifpOffer.promotions,
  };
}

/*
**
Note:
- RelatedIfp query (POST /v1/products/offers/ifp/search) only returns the available IFP options WITHOUT price object.
- The prices of the different IFP options are available on associatedPriceRules OR associatedPrices under the offerDetail object of the product (e.g the selected device). 
- Prices from associatedPriceRules are discounted prices after price point credit is deducted. This price only applies on specific plan and ifp combinations. 
- Prices from associatedPrice are general prices (device price divided by ifp duration), regardless to plan or any price point credit.
- Use associatedPriceRules if is available, if there is no rules associated with the given plan and ifp, then get price from associatedPrices.
**
*/

export function constructIfpOptions(
  associatedPriceRules: DeviceAssociatedPriceRules,
  associatedPrices: DeviceAssociatedPrices,
  availableIfpOffers: IfpOfferDetails,
  planId: string,
) {
  const ifpOptionsWithPrices = availableIfpOffers.reduce((prev, ifpOffer) => {
    if (isEmpty(associatedPriceRules) && isEmpty(associatedPrices)) {
      return prev;
    }

    const ifpOptionWithPriceRuleApplied =
      constructIfpOptionWithPriceRuleApplied(
        associatedPriceRules,
        ifpOffer,
        planId,
      );

    if (ifpOptionWithPriceRuleApplied) {
      prev.push(ifpOptionWithPriceRuleApplied);
      return prev;
    }

    const ifpOptionWithGeneralPrice = constructIfpOptionWithGeneralPrice(
      associatedPrices,
      ifpOffer,
    );

    if (ifpOptionWithGeneralPrice) {
      prev.push(ifpOptionWithGeneralPrice);
    }

    return prev;
  }, [] as PaymentOption[]);

  return sortBy(ifpOptionsWithPrices, ({ priceLength }) => priceLength);
}

export const updateStepSections = (sections: Section[]) =>
  sections.reduce(
    (section, item) => (section.includes(item) ? section : [...section, item]),
    [],
  );
